import {
  buildSurveyQuestionSchemas,
  questionKey,
  timestampToDate,
} from "./housingDevelopmentReporting";

const PERSONAL_TYPES = new Set(["email", "phone", "tel"]);

function clean(value) {
  return String(value ?? "").normalize("NFKC").trim();
}

export function selectHousingSurveyForCsv(surveys = [], surveyId = "") {
  const selectedId = clean(surveyId);
  if (!selectedId) return null;
  return surveys.find((survey) => clean(survey.surveyId || survey.id) === selectedId) || null;
}

function flatValue(value) {
  if (Array.isArray(value)) return value.join("; ");
  if (value && typeof value === "object") return JSON.stringify(value);
  return value ?? "";
}

export function csvSafeCell(value) {
  const text = String(flatValue(value));
  const protectedText = /^\s*[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${protectedText.replace(/"/g, '""')}"`;
}

function responseDate(value) {
  const date = timestampToDate(value);
  return date ? date.toISOString() : "";
}

function partnerNames(record) {
  const value = record?.partnerOrganisations || record?.partners || [];
  return (Array.isArray(value) ? value : [value])
    .map((item) => clean(typeof item === "object" ? item.name || item.label : item))
    .filter(Boolean);
}

function isPersonalQuestion(question) {
  const type = clean(question?.type).toLowerCase();
  return question?.personalData === true
    || question?.isPersonalData === true
    || PERSONAL_TYPES.has(type);
}

function answerValue(response, question, index) {
  const key = questionKey(question, index);
  if (response?.answers && Object.prototype.hasOwnProperty.call(response.answers, key)) {
    return response.answers[key];
  }
  return response?.[key] ?? "";
}

function responseVersion(response = {}) {
  const value = Number(response.surveyVersion ?? response.version ?? response.schemaVersion ?? 1);
  return Number.isInteger(value) && value > 0 ? value : 1;
}

function responsePartners(response, survey, event) {
  return partnerNames(response).length
    ? partnerNames(response)
    : partnerNames(survey).length
      ? partnerNames(survey)
      : partnerNames(event);
}

export function housingSurveyResponsesToCsv(
  responses = [],
  survey = {},
  event = {},
  { includePersonalData = false } = {},
) {
  const questions = buildSurveyQuestionSchemas(survey, responses)
    .filter(({ question }) => includePersonalData || !isPersonalQuestion(question));
  const fixedColumns = [
    ["Response ID", (response) => response.responseId || response.id || ""],
    ["Submission Date", (response) => responseDate(response.submittedAt || response.createdAt)],
    ["Submission Source", (response) => response.submissionSource || response.source || "unknown"],
    ["Event", (response) => response.eventName || event.eventName || event.title || ""],
    ["Event ID", (response) => response.eventId || event.eventId || event.id || ""],
    ["Survey", (response) => response.surveyName || survey.surveyName || survey.title || ""],
    ["Survey ID", (response) => response.surveyId || survey.surveyId || survey.id || ""],
    ["Survey Version", (response) => responseVersion(response)],
    ["Programme", (response) => response.programme || survey.programme || event.programme || ""],
    ["Partner organisations", (response) => responsePartners(response, survey, event).join("; ")],
  ];
  const personalColumns = includePersonalData ? [
    ["Contact name", (response) => response.contactDetails?.name || response.contactName || ""],
    ["Contact email", (response) => response.contactDetails?.email || response.contactEmail || ""],
    ["Contact phone", (response) => response.contactDetails?.mobile || response.contactDetails?.phone || response.contactPhone || ""],
  ] : [];
  const columns = [
    ...fixedColumns,
    ...personalColumns,
    ...questions.map(({ question, index, label, versions }) => [
      label,
      (response) => versions.includes(responseVersion(response))
        ? answerValue(response, question, index)
        : "",
    ]),
  ];
  const header = columns.map(([label]) => csvSafeCell(label)).join(",");
  const body = responses.map((response) => columns
    .map(([, value]) => csvSafeCell(value(response)))
    .join(","));
  return `\uFEFF${[header, ...body].join("\r\n")}`;
}

function safeFilenamePart(value, fallback) {
  const cleaned = clean(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cleaned || fallback;
}

export function housingSurveyCsvFilename(survey = {}, event = {}) {
  const surveyPart = safeFilenamePart(
    survey.surveySlug || survey.slug || survey.surveyName || survey.title,
    "housing-development-survey",
  );
  const date = clean(event.date || event.startDate).match(/^\d{4}-\d{2}-\d{2}$/)?.[0] || "undated";
  return `${surveyPart}-${date}.csv`;
}

export function downloadHousingSurveyCsv(csv, filename) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

